import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Slider } from "@/components/ui/slider";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MapPin, Clock, Search } from "lucide-react";
import AdSlot from "@/components/AdSlot";
import usePullToRefresh from "@/hooks/usePullToRefresh";
import PullIndicator from "@/components/PullIndicator";

export default function Feed() {
  const [tasks, setTasks] = useState([]);
  const [categories, setCategories] = useState([]);
  const [q, setQ] = useState({ city: "", category: "all", min_budget: [0] });

  const load = useCallback(() => {
    const params = {};
    if (q.city) params.city = q.city;
    if (q.category && q.category !== "all") params.category = q.category;
    if (q.min_budget[0] > 0) params.min_budget = q.min_budget[0];
    return api.get("/tasks", { params }).then(({ data }) => setTasks(data));
  }, [q]);

  const { pullY, progress, isRefreshing } = usePullToRefresh(load);

  useEffect(() => {
    api.get("/categories").then(({ data }) => setCategories(data));
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-6">
      <PullIndicator pullY={pullY} progress={progress} isRefreshing={isRefreshing} />
      <div>
        <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight">Open tasks near you</h1>
        <p className="text-slate-600 mt-1">Browse jobs posted by locals.</p>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200/80 p-3 sm:p-5 grid grid-cols-1 md:grid-cols-12 gap-3 items-center">
        <div className="md:col-span-4 relative">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input placeholder="City or neighborhood…" value={q.city} onChange={(e) => setQ({ ...q, city: e.target.value })} className="pl-9 h-11" data-testid="feed-city-input" />
        </div>
        <div className="md:col-span-3">
          <Select value={q.category} onValueChange={(v) => setQ({ ...q, category: v })}>
            <SelectTrigger className="h-11" data-testid="feed-category-select"><SelectValue placeholder="Category" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All categories</SelectItem>
              {categories.map((c) => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="md:col-span-5 flex items-center gap-3">
          <span className="text-xs font-semibold text-slate-500 whitespace-nowrap">Min $</span>
          <Slider value={q.min_budget} onValueChange={(v) => setQ({ ...q, min_budget: v })} max={500} step={10} className="flex-1" data-testid="feed-budget-slider" />
          <span className="font-mono-money text-sm text-slate-900 w-10 text-right">${q.min_budget[0]}</span>
        </div>
      </div>

      <AdSlot />

      {tasks.length === 0 ? (
        <div className="text-center py-16 text-slate-500">No open tasks match those filters.</div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {tasks.map((t, i) => (
            <Link key={t.task_id} to={`/tasks/${t.task_id}`} data-testid={`task-card-${t.task_id}`} className={`bg-white border border-slate-200/80 rounded-2xl p-5 card-hover float-in stagger-${(i % 4) + 1}`}>
              <div className="flex items-start justify-between gap-3">
                <Badge className="bg-orange-50 text-orange-700 border-orange-200 hover:bg-orange-50 font-semibold">{t.category}</Badge>
                <div className="font-mono-money text-sm px-3 py-1 rounded-xl bg-[#3E2A4E] text-[#D9FF3D]">${t.budget}</div>
              </div>
              <h3 className="font-heading font-bold text-lg mt-3 text-slate-900 leading-snug">{t.title}</h3>
              <p className="text-sm text-slate-600 mt-1.5 line-clamp-2">{t.description}</p>
              <div className="flex items-center gap-3 mt-4 text-xs text-slate-500">
                <span className="inline-flex items-center gap-1"><MapPin size={12} /> {t.location}</span>
                {t.scheduled_date && <span className="inline-flex items-center gap-1"><Clock size={12} /> {t.scheduled_date}</span>}
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
